import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Shortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  category: string;
  action: () => void;
}

interface KeyboardShortcutsOptions {
  onOpenSearch?: () => void;
  onCloseModal?: () => void;
}

export function useKeyboardShortcuts(options: KeyboardShortcutsOptions = {}) {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);

  const shortcuts: Shortcut[] = [
    // Navigation
    {
      key: 'g',
      alt: true,
      description: 'Go to Dashboard',
      category: 'Navigation',
      action: () => navigate('/dashboard'),
    },
    {
      key: 'r',
      alt: true,
      description: 'Go to Records',
      category: 'Navigation',
      action: () => navigate('/records'),
    },
    {
      key: 't',
      alt: true,
      description: 'Go to Tasks',
      category: 'Navigation',
      action: () => navigate('/tasks'),
    },
    {
      key: 's',
      alt: true,
      description: 'Go to Search',
      category: 'Navigation',
      action: () => navigate('/search'),
    },

    // Actions
    {
      key: 'k',
      ctrl: true,
      description: 'Open Search',
      category: 'Actions',
      action: () => options.onOpenSearch?.() || navigate('/search'),
    },
    {
      key: '/',
      description: 'Open Search',
      category: 'Actions',
      action: () => options.onOpenSearch?.() || navigate('/search'),
    },
    {
      key: '?',
      shift: true,
      description: 'Show Keyboard Shortcuts',
      category: 'Actions',
      action: () => setShowHelp(true),
    },

    // General
    {
      key: 'Escape',
      description: 'Close Modal / Dismiss',
      category: 'General',
      action: () => {
        setShowHelp(false);
        options.onCloseModal?.();
      },
    },
  ];

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Only allow Escape in inputs
        if (event.key !== 'Escape') {
          return;
        }
      }

      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;

        // Handle special case for '?' which requires shift
        const keyMatch =
          shortcut.key === '?' && event.key === '?' ||
          shortcut.key.toLowerCase() === event.key.toLowerCase();

        if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
          event.preventDefault();
          shortcut.action();
          return;
        }
      }
    },
    [shortcuts, options]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    shortcuts,
    showHelp,
    setShowHelp,
  };
}

// Helper to format shortcut key display
export function formatShortcutKey(shortcut: Pick<Shortcut, 'key' | 'ctrl' | 'shift' | 'alt'>): string {
  const parts: string[] = [];

  if (shortcut.ctrl) {
    // Show Cmd on Mac, Ctrl on others
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
    parts.push(isMac ? 'Cmd' : 'Ctrl');
  }
  if (shortcut.alt) {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
    parts.push(isMac ? 'Option' : 'Alt');
  }
  if (shortcut.shift) {
    parts.push('Shift');
  }

  // Format special keys
  const keyDisplay = shortcut.key === 'Escape' ? 'Esc' : shortcut.key.toUpperCase();
  parts.push(keyDisplay);

  return parts.join(' + ');
}
